import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import LoanGuarantorApi from "../../../../../../infrastructure/api/loanGuarantor";
import { LOAN_GUARANTEE_CHEQUE_INTENT } from "../../customerRequest/loanGuaranteeChequeIntentConstants";
import {
  OPERATOR_CARD_SX,
  OPERATOR_META_LABEL_SX,
} from "./operatorReviewStyles";

const pick = (obj, ...keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return "";
};

const emptyForm = () => ({
  nationalCode: "",
  mobile: "",
  firstName: "",
  lastName: "",
});

const LoanGuarantorCard = ({
  requestInfoId,
  apiPath,
  needsLoan = false,
  loanGuaranteeChequeIntent,
  isEditable = false,
  onSaved = null,
}) => {
  const intent = String(
    loanGuaranteeChequeIntent ?? ""
  );
  const requiresGuarantor =
    needsLoan && intent === LOAN_GUARANTEE_CHEQUE_INTENT.GuarantorWithSayadCheque;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const loadGuarantor = useCallback(async () => {
    if (!requiresGuarantor || !requestInfoId || !apiPath) {
      setForm(emptyForm());
      return;
    }

    setLoading(true);
    try {
      const data = await LoanGuarantorApi.get(apiPath, requestInfoId);
      if (!data) {
        setForm(emptyForm());
        return;
      }

      setForm({
        nationalCode: pick(data, "nationalCode", "NationalCode"),
        mobile: pick(data, "mobile", "Mobile"),
        firstName: pick(data, "firstName", "FirstName"),
        lastName: pick(data, "lastName", "LastName"),
      });
    } catch {
      setForm(emptyForm());
    } finally {
      setLoading(false);
    }
  }, [apiPath, requestInfoId, requiresGuarantor]);

  useEffect(() => {
    loadGuarantor();
  }, [loadGuarantor]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSave = async () => {
    if (!isEditable || !requestInfoId) return;

    setSaving(true);
    try {
      const res = await LoanGuarantorApi.save(apiPath, {
        requestInfoId: Number(requestInfoId),
        nationalCode: form.nationalCode,
        mobile: form.mobile,
        firstName: form.firstName,
        lastName: form.lastName,
        runCreditInquiry: true,
      });

      if (res?.isSuccess === false) {
        throw new Error(res?.message || res?.Message || "ذخیره اطلاعات ضامن ناموفق بود.");
      }

      toast.success(res?.message || res?.Message || "اطلاعات ضامن ذخیره شد.");
      await loadGuarantor();
      onSaved?.();
    } catch (err) {
      toast.error(err?.message || "ذخیره اطلاعات ضامن ناموفق بود.");
    } finally {
      setSaving(false);
    }
  };

  if (!requiresGuarantor) return null;

  return (
    <Box sx={{ ...OPERATOR_CARD_SX, p: { xs: 2, md: 2.5 } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <PersonAddAltOutlinedIcon color="primary" />
        <Typography variant="subtitle1" fontWeight={800}>
          اطلاعات ضامن
        </Typography>
        {loading ? <CircularProgress size={18} sx={{ mr: "auto" }} /> : null}
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        برای این درخواست، استعلام اعتباری روی ضامن انجام می‌شود. پس از ثبت
        اطلاعات، استعلام به‌صورت خودکار اجرا می‌شود.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="کد ملی ضامن"
            value={form.nationalCode}
            onChange={handleChange("nationalCode")}
            disabled={!isEditable || saving}
            inputProps={{ inputMode: "numeric" }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="موبایل ضامن"
            value={form.mobile}
            onChange={handleChange("mobile")}
            disabled={!isEditable || saving}
            inputProps={{ inputMode: "tel" }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="نام"
            value={form.firstName}
            onChange={handleChange("firstName")}
            disabled={!isEditable || saving}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="نام خانوادگی"
            value={form.lastName}
            onChange={handleChange("lastName")}
            disabled={!isEditable || saving}
          />
        </Grid>
      </Grid>

      {!isEditable ? (
        <Alert severity="info" variant="outlined">
          <Typography variant="body2">
            <Typography component="span" sx={OPERATOR_META_LABEL_SX}>
              نام کامل:
            </Typography>{" "}
            {[form.firstName, form.lastName].filter(Boolean).join(" ") || "—"}
          </Typography>
        </Alert>
      ) : (
        <Button
          variant="contained"
          size="small"
          startIcon={
            saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />
          }
          disabled={saving || loading}
          onClick={handleSave}
        >
          {saving ? "در حال ذخیره..." : "ذخیره و استعلام ضامن"}
        </Button>
      )}
    </Box>
  );
};

LoanGuarantorCard.propTypes = {
  requestInfoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  apiPath: PropTypes.string,
  needsLoan: PropTypes.bool,
  loanGuaranteeChequeIntent: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  isEditable: PropTypes.bool,
  onSaved: PropTypes.func,
};

export default LoanGuarantorCard;
